import { Filter, Mail, RefreshCw, Search } from "lucide-react";
import { useState } from "react";

import { ApiError } from "@/api/client";
import { useEmailEvents } from "@/api/hooks";
import type { EmailEventResponse } from "@/api/types";

/** Email deliverability viewer — backed by ``/admin/email-events``,
 *  populated by the Resend webhook handler at
 *  ``POST /webhooks/resend``.
 *
 *  Answers "did this customer get the magic-link / quote / etc." in
 *  one place instead of bouncing through Resend's dashboard. Bounces
 *  + complaints are highlighted because those are the events worth a
 *  human eyeball. */
export function EmailEventsPage() {
  const [eventTypeFilter, setEventTypeFilter] = useState("");
  const [recipientFilter, setRecipientFilter] = useState("");
  const [limit, setLimit] = useState(100);

  const events = useEmailEvents({
    event_type: eventTypeFilter || undefined,
    recipient_email: recipientFilter || undefined,
    limit,
  });

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-mono text-2xl font-bold tracking-tight">Email events</h1>
        <p className="mt-1 max-w-3xl text-sm text-graphite-soft">
          Resend deliverability events for everything PencilOut sends — magic links, quote
          notifications, dispute alerts. Newest first. Bounces and complaints are highlighted in red
          and worth eyeballing.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-rule bg-paper-warm p-4">
        <div className="flex-1 min-w-[180px]">
          <span className="label-mono mb-1 block">Event type contains</span>
          <div className="relative">
            <Filter
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-graphite-soft"
              aria-hidden
            />
            <input
              type="text"
              value={eventTypeFilter}
              onChange={(e) => setEventTypeFilter(e.target.value)}
              placeholder="bounce, delivered, opened, …"
              className="w-full rounded border border-rule bg-paper py-1.5 pl-8 pr-2 font-mono text-sm focus:border-graphite focus:outline-none"
            />
          </div>
        </div>
        <div className="flex-1 min-w-[200px]">
          <span className="label-mono mb-1 block">Recipient email (exact)</span>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-graphite-soft"
              aria-hidden
            />
            <input
              type="email"
              value={recipientFilter}
              onChange={(e) => setRecipientFilter(e.target.value)}
              placeholder="someone@example.com"
              className="w-full rounded border border-rule bg-paper py-1.5 pl-8 pr-2 font-mono text-sm focus:border-graphite focus:outline-none"
            />
          </div>
        </div>
        <div className="w-24">
          <span className="label-mono mb-1 block">Limit</span>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="w-full rounded border border-rule bg-paper py-1.5 px-2 font-mono text-sm focus:border-graphite focus:outline-none"
          >
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={250}>250</option>
            <option value={500}>500</option>
          </select>
        </div>
        <button
          type="button"
          onClick={() => events.refetch()}
          disabled={events.isFetching}
          className="inline-flex items-center gap-1.5 rounded border border-rule bg-paper px-3 py-1.5 font-mono text-xs font-semibold text-graphite transition hover:bg-pencil-yellow-soft disabled:opacity-40"
        >
          <RefreshCw
            className={`h-3.5 w-3.5 ${events.isFetching ? "animate-spin" : ""}`}
            aria-hidden
          />
          Refresh
        </button>
      </div>

      {events.isLoading && <p className="text-sm text-graphite-soft">Loading events…</p>}
      {events.error && (
        <p className="rounded border border-red-pencil/30 bg-red-pencil/5 px-3 py-2 text-sm text-red-pencil">
          {events.error instanceof ApiError ? events.error.detail : "Could not load events."}
        </p>
      )}
      {events.data && events.data.length === 0 && (
        <p className="rounded-lg border border-rule bg-paper-warm p-5 text-sm text-graphite-soft">
          No events match this filter. (If this is a fresh deploy, the Resend webhook may not have
          been registered yet — see <code>docs/ops/secrets-rotation.md</code>.)
        </p>
      )}

      {events.data && events.data.length > 0 && (
        <div className="rounded-lg border border-rule bg-paper-warm">
          <div className="border-b border-rule px-4 py-2 text-xs text-graphite-soft">
            <span className="label-mono">{events.data.length} events</span>
          </div>
          <ul>
            {events.data.map((evt) => (
              <EmailEventRow key={evt.id} event={evt} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function EmailEventRow({ event }: { event: EmailEventResponse }) {
  const [open, setOpen] = useState(false);
  const isBad = event.event_type === "email.bounced" || event.event_type === "email.complained";
  const parsedPayload = parsePayload(event.payload_json);

  return (
    <li className={`border-b border-rule/60 last:border-b-0 ${isBad ? "bg-red-pencil/5" : ""}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-paper"
      >
        <Mail
          className={`mt-0.5 h-4 w-4 flex-none ${isBad ? "text-red-pencil" : "text-graphite-soft"}`}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span
              className={`font-mono text-sm font-semibold tabular-nums ${isBad ? "text-red-pencil" : ""}`}
            >
              {event.event_type}
            </span>
            {event.bounce_type && (
              <span className="rounded bg-red-pencil/10 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-red-pencil">
                {event.bounce_type}
              </span>
            )}
            <span className="font-mono text-xs text-graphite-soft tabular-nums">
              {new Date(event.created_at).toLocaleString()}
            </span>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-graphite-soft">
            <span className="font-mono">{event.recipient_email ?? "—"}</span>
            {event.subject && <span className="truncate italic">"{event.subject}"</span>}
          </div>
        </div>
        <span
          className={`mt-1 font-mono text-[10px] uppercase tracking-wider text-graphite-faint transition ${open ? "rotate-90" : ""}`}
          aria-hidden
        >
          ▶
        </span>
      </button>
      {open && parsedPayload !== null && (
        <pre className="overflow-x-auto border-t border-rule/40 bg-paper px-4 py-3 font-mono text-[11px] text-graphite-soft">
          {JSON.stringify(parsedPayload, null, 2)}
        </pre>
      )}
      {open && parsedPayload === null && (
        <p className="border-t border-rule/40 bg-paper px-4 py-3 font-mono text-[11px] text-graphite-faint">
          No payload.
        </p>
      )}
    </li>
  );
}

function parsePayload(raw: string | null): unknown {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}
